import { isHomeLandingPath } from '../components/Home/waitlist/useWaitlistHashScroll';
import {
  flushPreInitPostHogQueueOnPageLeave,
  initializePostHog,
} from './analyticsClient';
import { initializeSentry } from './sentryClient';

let scheduled = false;
let initStarted = false;
let initPromise: Promise<void> | null = null;

const INTERACTION_EVENTS = [
  'pointerdown',
  'keydown',
  'touchstart',
  'focusin',
] as const;

/** Minimum ms after window load before idle-path SDK download (fallback if LCP never fires). */
export const POST_LOAD_SDK_DELAY_MS = 5500;
/** Extra ms after the LCP render timestamp before idle-path SDK download. */
export const LCP_POST_BUFFER_MS = 2500;

/**
 * Home hero A/B: schedule PostHog soon after first paint so /flags can resolve
 * before {@link DEFERRED_FEATURE_FLAG_LOCK_MS} in `useDeferredFeatureFlag`.
 */
export const HOME_EARLY_ANALYTICS_IDLE_TIMEOUT_MS = 500;
/** Conservative budget for 2× rAF before the home idle callback is armed. */
export const HOME_EARLY_ANALYTICS_PAINT_BUDGET_MS = 100;

/** Worst-case ms from `load` until home-only idle-path analytics init runs. */
export function getHomeRouteMaxScheduledAnalyticsInitMs(): number {
  return HOME_EARLY_ANALYTICS_PAINT_BUDGET_MS + HOME_EARLY_ANALYTICS_IDLE_TIMEOUT_MS;
}

function isHomeRouteForEarlyAnalytics(): boolean {
  if (typeof window === 'undefined') return false;
  return isHomeLandingPath(window.location.pathname);
}

async function runInitOnce(): Promise<void> {
  if (initStarted) return initPromise ?? Promise.resolve();
  initStarted = true;
  initPromise = (async () => {
    await Promise.all([initializePostHog(), initializeSentry()]);
  })();
  return initPromise;
}

type CleanupFn = () => void;

function runCleanup(cleanups: CleanupFn[]): void {
  cleanups.forEach((fn) => fn());
  cleanups.length = 0;
}

/**
 * Schedules PostHog + Sentry lazy init once: first real user interaction,
 * page hide / visibility hidden, or after the last LCP update (+ buffer) / load fallback.
 */
export function scheduleLazyAnalyticsInit(): void {
  if (scheduled || typeof window === 'undefined') return;
  scheduled = true;

  const cleanups: CleanupFn[] = [];
  let triggered = false;

  const trigger = (): void => {
    if (triggered) return;
    triggered = true;
    runCleanup(cleanups);
    void runInitOnce();
  };

  for (const eventName of INTERACTION_EVENTS) {
    const handler = (): void => {
      trigger();
    };
    window.addEventListener(eventName, handler, { passive: true, once: true });
    cleanups.push(() => {
      window.removeEventListener(eventName, handler);
    });
  }

  const onPageLeave = (): void => {
    if (document.visibilityState === 'hidden') {
      flushPreInitPostHogQueueOnPageLeave();
      trigger();
    }
  };
  document.addEventListener('visibilitychange', onPageLeave);
  cleanups.push(() => {
    document.removeEventListener('visibilitychange', onPageLeave);
  });

  const onPageHide = (): void => {
    flushPreInitPostHogQueueOnPageLeave();
    trigger();
  };
  window.addEventListener('pagehide', onPageHide);
  cleanups.push(() => {
    window.removeEventListener('pagehide', onPageHide);
  });

  const startAfterLoad = (): void => {
    if (isHomeRouteForEarlyAnalytics()) {
      const runWhenIdle = (): void => {
        trigger();
      };
      const armIdle = (): void => {
        if (typeof window.requestIdleCallback === 'function') {
          const idleId = window.requestIdleCallback(runWhenIdle, {
            timeout: HOME_EARLY_ANALYTICS_IDLE_TIMEOUT_MS,
          });
          cleanups.push(() => {
            window.cancelIdleCallback(idleId);
          });
        } else {
          window.setTimeout(runWhenIdle, HOME_EARLY_ANALYTICS_IDLE_TIMEOUT_MS);
        }
      };
      window.requestAnimationFrame(() => {
        window.requestAnimationFrame(armIdle);
      });
      return;
    }

    let idleTimer: number | undefined;
    let lcpSeen = false;

    const clearIdleTimer = (): void => {
      if (idleTimer !== undefined) {
        window.clearTimeout(idleTimer);
        idleTimer = undefined;
      }
    };

    const armFallbackFromLoad = (): void => {
      if (lcpSeen) return;
      clearIdleTimer();
      idleTimer = window.setTimeout(() => {
        trigger();
      }, POST_LOAD_SDK_DELAY_MS);
    };

    const armAfterLatestLcp = (): void => {
      lcpSeen = true;
      clearIdleTimer();
      idleTimer = window.setTimeout(() => {
        trigger();
      }, LCP_POST_BUFFER_MS);
    };

    try {
      const lcpObserver = new PerformanceObserver((list) => {
        if (list.getEntries().length === 0) return;
        armAfterLatestLcp();
      });
      lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      cleanups.push(() => {
        lcpObserver.disconnect();
      });
    } catch {
      armFallbackFromLoad();
      return;
    }

    armFallbackFromLoad();
  };

  if (document.readyState === 'complete') {
    startAfterLoad();
  } else {
    window.addEventListener('load', startAfterLoad, { once: true });
  }
}

/** Await lazy init (tests / runtime proof). */
export function ensureAnalyticsInitialized(): Promise<void> {
  if (initStarted) return initPromise ?? Promise.resolve();
  return runInitOnce();
}

export function resetLazyAnalyticsInitForTests(): void {
  scheduled = false;
  initStarted = false;
  initPromise = null;
}

export function isLazyAnalyticsInitStartedForTests(): boolean {
  return initStarted;
}
